import type { HealthResponse } from '@aibi/contracts';
import type { AppError } from '@aibi/shared';
import { useCallback, useEffect, useState } from 'react';
import type { HealthApi } from '../../api/health-api';

export type ServerHealthState =
  | { readonly status: 'checking' }
  | { readonly status: 'online'; readonly health: HealthResponse }
  | { readonly status: 'offline'; readonly error: AppError };

/**
 * Consulta el estado real del servidor. Cancela la peticion en curso al desmontar
 * o al reintentar, evitando actualizaciones de estado obsoletas.
 *
 * @example
 * const { state, refresh } = useServerHealth(healthApi);
 */
export function useServerHealth(api: HealthApi): {
  state: ServerHealthState;
  refresh: () => void;
} {
  const [state, setState] = useState<ServerHealthState>({ status: 'checking' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    void api.getHealth(controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      setState(
        result.ok
          ? { status: 'online', health: result.value }
          : { status: 'offline', error: result.error },
      );
    });
    return () => {
      controller.abort();
    };
  }, [api, attempt]);

  const refresh = useCallback(() => {
    setState({ status: 'checking' });
    setAttempt((value) => value + 1);
  }, []);

  return { state, refresh };
}
